/*
 * Phase 6 / P6-E1 (May 6, 2026): consolidated cart handlers.
 *
 * Replaces the duplicated handler IIFEs in sections/{side_cart,cart_content}.liquid.
 * Both section files previously defined nearly-identical copies of cascadeRemove,
 * syncGroupQuantity, removeHandler, qtyChangeHandler, addonRemoveHandler,
 * sweepOrphans (~500 lines of duplication). They also re-evaluated on every
 * section refresh via $s.globalEval, requiring `_babeinaXxxBound` flags to
 * prevent the duplicate-handler bug noted at fragility-matrix line 20.
 *
 * Design:
 * - Bind ONCE at DOMContentLoaded via event delegation on the document.
 * - Capture-phase handlers detect cart context (#side-cart vs #cart) from
 *   the event target and route refresh() / source labels accordingly.
 * - No more re-binding on section refresh — the handlers stay attached to
 *   `document`, which persists across section swaps. The `_babeinaXxxBound`
 *   guards in the section IIFEs become unnecessary.
 * - cart:updated event bus (P6-E3) is emitted from the same callsites as
 *   before, with the same source labels for backward compatibility.
 *
 * Coexistence with legacy:
 * - Asset-level controllers (assets/{side_cart,cart_content}.js.liquid) keep
 *   their updateSideCart / updateCart / updateItemsQuantity / showSideCart
 *   methods. Only the duplicated handler logic is moved here.
 * - Per-item engraving cascade <script> tags inside snippets/cart_item.liquid
 *   are LEFT ALONE for E1 (they're markup-specific and have their own
 *   __engrCascadeBound guards). E1's document-level capture handlers fire
 *   first and stopImmediatePropagation, so the per-item REMOVE cascade
 *   scripts effectively no-op anyway.
 *
 * Resolves:
 * - KI-3 (three competing handler systems on the same buttons)
 * - The cross-section gotcha at CART_FRAGILITY_MATRIX.md line 20
 *
 * Doesn't yet retire $s.globalEval entirely — it's still used by the asset
 * controllers' section refresh pipeline (which now does much less work
 * because there are no IIFE handlers to re-eval). That cleanup can ship in
 * a follow-up PR if ever needed.
 */
(function(){
  'use strict';
  if (window.__babeinaCartHandlersBound) return;
  window.__babeinaCartHandlersBound = true;

  // ===========================================================================
  // Helpers (single source of truth)
  // ===========================================================================

  function fetchCart(){
    return fetch('/cart.js').then(function(r){ return r.json(); });
  }

  function getCartRoot(target){
    if (!target || !target.closest) return null;
    // Phase 6 / E2.6 (May 7, 2026): cart_v3 D2 (#cart-v3-d2) is a third
    // valid cart root.
    return target.closest('#side-cart, #cart, #cart-v3-d2');
  }

  function getRootKind(root){
    if (!root) return null;
    if (root.id === 'side-cart') return 'side-cart';
    if (root.id === 'cart') return 'cart-page';
    // May 12, 2026 perf fix: D2 gets its own kind so refresh() can fire
    // the cart_v3 section refresh DIRECTLY (in parallel with the
    // background side-cart refresh) instead of waiting for side-cart's
    // /sections=side_cart fetch + MutationObserver chain. Cuts ~300-
    // 400ms of wall time off every D2 +/-/remove. Source labels still
    // use side-cart shape since cart_handlers' source labels are
    // already aligned with that taxonomy.
    if (root.id === 'cart-v3-d2') return 'cart-v3-d2';
    return null;
  }

  // For source-label purposes only: D2 mutations still emit the
  // `side-cart-*` source labels (e.g. 'side-cart-group-qty') because
  // semantically a D2 mutation IS a side-cart mutation — the side cart
  // is the underlying data source, D2 is just the presentation layer.
  // Keeping the labels stable avoids breaking any downstream listener
  // that filters on source.
  function getSourceKind(root){
    if (!root) return null;
    if (root.id === 'cart') return 'cart-page';
    return 'side-cart';
  }

  // Refresh the section the click happened in. Falls back to the other
  // controller if the expected one isn't present (e.g. side cart action on
  // a page where SideCart hasn't loaded yet — shouldn't happen but defensive).
  //
  // opts.skipItems (May 12, 2026 perf fix):
  //   For pure qty bumps (no add/remove), tell the D2 refresh to skip
  //   re-rendering the items slot. The optimistic UI helpers already
  //   patched per-row qty + price; skipping the swap avoids the
  //   lazysizes <img> teardown/re-attach that causes the visible
  //   "refresh-like flicker" on +/- clicks. Summary, upsell, and
  //   free-shipping slots still refresh because they may transition
  //   (discount tier crossings, "X Item(s)" heading, free-ship status).
  function refresh(root, opts){
    opts = opts || {};
    var kind = getRootKind(root);
    try {
      // D2 path: refresh D2 DIRECTLY (parallel) AND fire side-cart
      // refresh in the background so D1's items source stays in sync
      // for the next ATC. Both fetches happen concurrently — no chain.
      if (kind === 'cart-v3-d2') {
        if (typeof window.__babeinaCartV3RefreshD2 === 'function') {
          window.__babeinaCartV3RefreshD2({ skipItems: !!opts.skipItems });
        }
        if (window.SideCart && typeof window.SideCart.refresh === 'function') {
          window.SideCart.refresh();
        }
        return;
      }
      if (kind === 'side-cart' && window.SideCart && typeof window.SideCart.refresh === 'function') {
        window.SideCart.refresh();
        return;
      }
      if (kind === 'cart-page' && window.CartPage && typeof window.CartPage.updateCart === 'function') {
        window.CartPage.updateCart();
        return;
      }
      // Defensive fallback: try whichever controller is present.
      if (window.CartPage && typeof window.CartPage.updateCart === 'function') {
        window.CartPage.updateCart();
        return;
      }
      if (window.SideCart && typeof window.SideCart.refresh === 'function') {
        window.SideCart.refresh();
        return;
      }
      location.reload();
    } catch(_) {
      location.reload();
    }
  }

  function syncHeader(cart){
    try {
      if (window.$s && typeof window.$s.refreshHeaderCartCount === 'function') {
        window.$s.refreshHeaderCartCount(cart);
      }
    } catch(_) {}
  }

  function emitUpdated(cart, source){
    if (window.$s && typeof window.$s.emitCartUpdated === 'function') {
      window.$s.emitCartUpdated(cart, source);
    }
  }

  function handleError(err, rowEls){
    if (window.$s && typeof window.$s.handleCartError === 'function') {
      window.$s.handleCartError(err, rowEls);
    }
  }

  // ===========================================================================
  // Cascade Remove (REMOVE button on grouped parent items)
  //
  // Mirrors the old cascadeRemove in sections/{side_cart,cart_content}.liquid.
  // Removes the parent + all addon children sharing its _group_id in a single
  // POST /cart/update.js call (P6-B7 parallelization). Optimistically fades
  // the parent row + addon children + addon chips together (P6-C2). Defers
  // the section refresh by SECTION_REFRESH_DEFER_MS so the fade animation
  // completes before the DOM swap.
  // ===========================================================================
  function cascadeRemove(container, root){
    var gid = (container && container.getAttribute('data-group-id')) || '';
    var pkey = (container && container.getAttribute('data-key')) || '';
    // Use the source-label kind (D2 still emits side-cart-* labels for
    // analytics continuity) — kind drives source labels only, not the
    // refresh path.
    var sourceKind = getSourceKind(root);
    var sourceLabel = sourceKind === 'side-cart'
      ? 'side-cart-cascade-remove'
      : 'cart-page-cascade-remove';

    if (container && container.classList) container.classList.add('is-updating');

    // Skeleton-loading state for D2 order summary + checkout button.
    if (window.$s && typeof window.$s.setD2Loading === 'function') {
      window.$s.setD2Loading(true);
    }

    // P6-C2 optimistic fade.
    var removeUndo = null;
    var totalsUndo = null;
    if (window.$s && typeof window.$s.applyOptimisticRemove === 'function') {
      var groupEls = [container];
      try {
        if (gid && root && root.querySelectorAll) {
          var siblings = root.querySelectorAll('.item.group-addon[data-group-id="' + gid + '"]');
          siblings.forEach(function(el){ groupEls.push(el); });
        }
        var chips = container.querySelectorAll('.property');
        chips.forEach(function(el){ groupEls.push(el); });
      } catch(_) {}
      totalsUndo = (typeof window.$s.applyOptimisticCartTotals === 'function')
        ? window.$s.applyOptimisticCartTotals([container], 0)
        : null;
      removeUndo = window.$s.applyOptimisticRemove(groupEls);
    }

    var refreshDeferred = function(cart){
      if (cart) syncHeader(cart);
      if (cart) emitUpdated(cart, sourceLabel);
      // Defer the section refresh long enough for the optimistic remove
      // animation to finish before the DOM swap stomps it. Gated on V2
      // OR V3 — the optimistic-remove helper itself is enabled for both
      // (May 12, 2026 gate-widening; see docs/CART_V2_ROLLOUT.md).
      if (window.$s && (window.$s.cartV2 || window.$s.cartV3) && typeof window.$s.SECTION_REFRESH_DEFER_MS === 'number') {
        setTimeout(function(){ refresh(root); }, window.$s.SECTION_REFRESH_DEFER_MS);
      } else {
        refresh(root);
      }
    };

    var rollbackRemove = function(){
      if (removeUndo) removeUndo();
      if (totalsUndo) totalsUndo();
    };

    return fetchCart().then(function(cart){
      var updates = {};
      try {
        (cart.items || []).forEach(function(it){
          var p = it.properties || {};
          if (it.key === pkey || (p._group_id === gid && !p._group)) {
            updates[it.key] = 0;
          }
        });
      } catch(_) {}
      if (pkey && !(pkey in updates)) updates[pkey] = 0;
      if (!Object.keys(updates).length) return Promise.resolve(null);
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates })
      }).then(function(r){
        if (r && r.ok === false) return Promise.reject(r);
        return r.json();
      });
    })
    .then(refreshDeferred)
    .catch(function(err){
      // P6-F2 inline error.
      rollbackRemove();
      if (container && container.classList) container.classList.remove('is-updating');
      // Clear the in-flight guard set in removeHandler/qtyChangeHandler
      // so the user can retry. On success path we don't bother — the
      // section refresh wipes the container element entirely.
      if (container && container.dataset) container.dataset.cartRemoveInFlight = '0';
      handleError(err, [container]);
      refresh(root);
    });
  }

  // ===========================================================================
  // Sync Grouped Quantity (qty +/- on a grouped parent — fans out to children)
  //
  // GET /cart.js to see all line items (some addon children are hidden from
  // DOM), build full updates[] array with the new qty for any line in this
  // group, leave others untouched. Uses POST /cart/update.js's positional
  // updates form because we need to set quantities by index.
  // ===========================================================================
  function syncGroupQuantity(container, newQty, root){
    var gid = (container && container.getAttribute('data-group-id')) || '';
    if (!gid) return Promise.resolve();

    var sourceKind = getSourceKind(root);
    var sourceLabel = sourceKind === 'side-cart'
      ? 'side-cart-group-qty'
      : 'cart-page-group-qty';

    if (container && container.classList) container.classList.add('is-updating');

    // Skeleton-loading state for D2 order summary + checkout button.
    // No-op when V3 is off (selectors are V3-scoped in CSS).
    if (window.$s && typeof window.$s.setD2Loading === 'function') {
      window.$s.setD2Loading(true);
    }

    var optimisticUndo = (window.$s && typeof window.$s.applyOptimisticCartTotals === 'function')
      ? window.$s.applyOptimisticCartTotals([container], newQty * 1)
      : null;

    return fetchCart().then(function(cart){
      var updates = [];
      (cart.items || []).forEach(function(it){
        var p = it.properties || {};
        if (p._group_id === gid) { updates.push(newQty * 1); }
        else { updates.push(it.quantity * 1); }
      });
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates })
      });
    })
    .then(function(r){ return r.ok ? r.json() : Promise.reject(r); })
    .then(function(cart){
      syncHeader(cart);
      emitUpdated(cart, sourceLabel);
      // Pure qty bump: optimistic UI handled per-row qty + price.
      // Skip the items innerHTML swap on D2 refresh to avoid lazysizes
      // re-attach flicker. Summary/upsell/free-ship still refresh.
      refresh(root, { skipItems: true });
    })
    .catch(function(err){
      if (optimisticUndo) optimisticUndo();
      if (container && container.classList) container.classList.remove('is-updating');
      // Roll back the displayed qty value to its pre-click state.
      // Otherwise — especially in V3, where the optimistic-totals helper
      // is disabled and we depend on the section refresh to restore truth —
      // the qty input stays stuck at the failed value when the section
      // refresh ALSO fails (e.g. offline). Resolves KI-19.
      try {
        var qtyInput = container.querySelector('.quantity-box input[type="number"]');
        if (qtyInput && qtyInput.dataset && qtyInput.dataset.prevQty) {
          qtyInput.value = qtyInput.dataset.prevQty;
          var quantityHost = qtyInput.closest('quantity-input');
          if (quantityHost && typeof quantityHost.refreshState === 'function') {
            quantityHost.refreshState();
          }
        }
      } catch (_) {}
      handleError(err, [container]);
      refresh(root);
    });
  }

  // ===========================================================================
  // Capture-phase event handlers (delegated on document)
  // ===========================================================================

  function removeHandler(e){
    var btn = e.target && e.target.closest
      ? e.target.closest('.item.group-main button.remove')
      : null;
    if (!btn) return;
    var root = getCartRoot(btn);
    if (!root) return;
    var container = btn.closest('.item.group-main');
    if (!container) return;
    var gid = container.getAttribute('data-group-id') || '';
    if (!gid) return; // Let normal non-group handlers run (legacy paths).
    e.preventDefault();
    e.stopPropagation();
    // In-flight guard (May 12, 2026 fix): removeHandler is bound to
    // mousedown + touchstart + click in init() so any single user
    // tap fires it 2-3 times. Without this guard cascadeRemove ran
    // multiple times — the second POST hit an already-removed line
    // item, errored, and rollbackRemove() stripped `.is-removing`,
    // causing the visible "fade → reappear → fade" sequence the
    // merchant reported. The flag is set synchronously here so the
    // mousedown call wins; any later touchstart/click for the same
    // tap finds it set and bails.
    if (container.dataset.cartRemoveInFlight === '1') return;
    container.dataset.cartRemoveInFlight = '1';
    cascadeRemove(container, root);
  }

  function qtyChangeHandler(e){
    var input = e.target && e.target.closest
      ? e.target.closest('.item.group-main .quantity-box input[type="number"]')
      : null;
    if (!input) return;
    var root = getCartRoot(input);
    if (!root) return;
    var container = input.closest('.item.group-main');
    if (!container) return;
    var gid = container.getAttribute('data-group-id') || '';
    if (!gid) return; // Let normal non-group handlers run.
    e.preventDefault();
    e.stopPropagation();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();
    var val = parseInt(input.value, 10);
    if (isNaN(val)) val = 0;
    // P6-C2 follow-up: trash-at-1 on grouped parents routes through
    // cascadeRemove for the optimistic-remove animation to fire on
    // parent + addon chips together.
    if (val === 0) {
      // Same in-flight guard as removeHandler — see comment there.
      if (container.dataset.cartRemoveInFlight === '1') return;
      container.dataset.cartRemoveInFlight = '1';
      cascadeRemove(container, root);
      return;
    }
    syncGroupQuantity(container, val, root);
  }

  function addonRemoveHandler(e){
    var btn = e.target && e.target.closest
      ? e.target.closest('.addon-remove')
      : null;
    if (!btn) return;
    var root = getCartRoot(btn);
    if (!root) return;
    var sourceKind = getSourceKind(root);
    var sourceLabel = sourceKind === 'side-cart'
      ? 'side-cart-addon-remove'
      : 'cart-page-addon-remove';

    e.preventDefault();
    e.stopPropagation();
    var key = btn.getAttribute('data-key');
    if (!key) return;

    // Dim + spin the chip while the request is in flight (P6-B2).
    var chip = btn.closest('.property') || btn.parentNode;
    if (chip && chip.classList) chip.classList.add('is-updating');

    // Skeleton-loading state for D2 order summary + checkout button.
    if (window.$s && typeof window.$s.setD2Loading === 'function') {
      window.$s.setD2Loading(true);
    }

    // Optimistic fade + collapse (P6-C2).
    var chipRemoveUndo = (window.$s && typeof window.$s.applyOptimisticRemove === 'function')
      ? window.$s.applyOptimisticRemove([chip])
      : null;

    var commit = function(){ refresh(root); };

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    })
    .then(function(r){
      if (r && r.ok === false) return Promise.reject(r);
      return r.json();
    })
    .then(function(cart){
      syncHeader(cart);
      emitUpdated(cart, sourceLabel);
      // V2 OR V3 — see comment in cascadeRemove above.
      if (window.$s && (window.$s.cartV2 || window.$s.cartV3) && typeof window.$s.SECTION_REFRESH_DEFER_MS === 'number') {
        setTimeout(commit, window.$s.SECTION_REFRESH_DEFER_MS);
      } else {
        commit();
      }
    })
    .catch(function(err){
      // P6-F2: inline error on the parent row (chip is being collapsed).
      if (chipRemoveUndo) chipRemoveUndo();
      if (chip && chip.classList) chip.classList.remove('is-updating');
      var parentRow = chip && chip.closest ? chip.closest('.item.group-main') : null;
      handleError(err, parentRow ? [parentRow] : []);
      refresh(root);
    });
  }

  function upsellAddHandler(e){
    var btn = e.target && e.target.closest
      ? e.target.closest('[data-action="cart-summary-upsell-add"]')
      : null;
    if (!btn || btn.disabled) return;
    var container = btn.closest('[data-cart-summary-upsell-item]');
    if (!container) return;
    var root = getCartRoot(btn);
    if (!root) return;

    e.preventDefault();
    e.stopPropagation();

    var variantId = parseInt(container.getAttribute('data-variant-id') || '0', 10);
    if (!variantId || isNaN(variantId)) return;

    var qtyInput = container.querySelector('[data-cart-summary-upsell-qty-wrap] .quantity-input');
    // Prefer the button's own data-qty (supports multiple add buttons
    // like +1 / +X). Fall back to the hidden quantity input value.
    var btnQtyAttr = btn.getAttribute('data-qty');
    var qty = parseInt(btnQtyAttr || (qtyInput && qtyInput.value) || '1', 10);
    if (isNaN(qty) || qty < 1) qty = 1;
    if (qtyInput) qtyInput.value = qty;

    var label = btn.querySelector('.summary-upsell-add-label');
    var originalText = label ? label.textContent : btn.textContent;
    function setLabel(text){
      if (label) { label.textContent = text; }
      else { btn.textContent = text; }
    }
    function resetButton(){
      btn.disabled = false;
      setLabel(originalText);
    }

    btn.disabled = true;
    setLabel('Adding...');
    if (window.$s && typeof window.$s.showLoadingOverlay === 'function') {
      window.$s.showLoadingOverlay();
    }
    // Skeleton-loading state for D2 order summary + checkout button.
    if (window.$s && typeof window.$s.setD2Loading === 'function') {
      window.$s.setD2Loading(true);
    }

    // Give the upsell line item a unique `_group_id` + `_group: 'main'`
    // so the V3 grouped remove/qty handlers (`removeHandler`, `qtyChangeHandler`)
    // recognize it as a one-line group. Without this, the row renders
    // without `data-group-id` and those handlers bail early
    // (`if (!gid) return;`), making the remove + qty UI inert.
    var groupId = (window.$s && typeof window.$s.randomstring === 'function')
      ? window.$s.randomstring(6)
      : ('u' + Math.random().toString(36).slice(2, 8));
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: qty,
        properties: { _group_id: groupId, _group: 'main' }
      })
    })
    .then(function(res){
      if (res.ok) return res.json();
      return res.json()
        .catch(function(){ return {}; })
        .then(function(err){ throw err; });
    })
    .then(function(){
      syncHeader();
      emitUpdated(null, 'cart-page-upsell-add');
      // resetButton in case the section refresh doesn't replace this DOM
      // (e.g. cart_summary_upsell_hide_if_in_cart is false).
      resetButton();
      try {
        if (window.CartPage && typeof window.CartPage.updateCart === 'function') {
          window.CartPage.updateCart();
        } else {
          refresh(root);
        }
      } catch(_) { refresh(root); }
    })
    .catch(function(err){
      resetButton();
      var msg = (err && (err.description || err.message))
        ? (err.description || err.message)
        : 'Unable to add item';
      if (window.$s && typeof window.$s.addToCartFail === 'function') {
        window.$s.addToCartFail(msg);
      } else if (window.$s && typeof window.$s.hideLoadingOverlay === 'function') {
        window.$s.hideLoadingOverlay();
      }
    });
  }

  // ===========================================================================
  // Sweep orphans (legacy state safety net, runs once per page load).
  //
  // Detects addon children whose parent has been removed (e.g. due to a
  // historical bug or partial update). Removes them so the cart isn't left
  // with floating addons. Window-level guard preserved so this runs at most
  // once per page load.
  // ===========================================================================
  function sweepOrphans(){
    function removeKeys(keys){
      var seq = Promise.resolve();
      (keys || []).forEach(function(k){
        seq = seq.then(function(){
          return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: k, quantity: 0 })
          });
        });
      });
      return seq;
    }
    function refreshAny(){
      try {
        if (window.CartPage && typeof window.CartPage.updateCart === 'function') {
          window.CartPage.updateCart();
        } else if (window.SideCart && typeof window.SideCart.refresh === 'function') {
          window.SideCart.refresh();
        }
      } catch(_) {}
    }
    fetchCart().then(function(cart){
      var parentGids = Object.create(null);
      (cart.items || []).forEach(function(it){
        var p = it.properties || {};
        if (p._group && p._group_id) { parentGids[p._group_id] = true; }
      });
      var orphans = [];
      (cart.items || []).forEach(function(it){
        var p = it.properties || {};
        if (p._group_id && !p._group &&
            (p.Source === 'Engraving Add-On' ||
             p.Source === 'Birthstone Add-On' ||
             p.Source === 'Letter Add-On' ||
             p.Source === 'Charm Add-On' ||
             p.Source === 'Extender' ||
             p.Source === 'Gift Wrap' ||
             p.Source === 'Personalization Add-On') &&
            !parentGids[p._group_id]) {
          orphans.push(it.key);
        }
      });
      if (orphans.length) {
        removeKeys(orphans).then(refreshAny);
      }
    }).catch(function(){});
  }

  // ===========================================================================
  // Init: bind once on DOMContentLoaded
  // ===========================================================================
  function init(){
    // Capture-phase delegation on document so handlers fire regardless of
    // which root (#side-cart or #cart) the click came from, AND survive any
    // section refresh because they're bound to document, not to the section
    // container.
    document.addEventListener('mousedown', removeHandler, true);
    document.addEventListener('touchstart', removeHandler, true);
    document.addEventListener('click', removeHandler, true);
    document.addEventListener('change', qtyChangeHandler, true);
    document.addEventListener('click', addonRemoveHandler, true);
    document.addEventListener('click', upsellAddHandler, true);

    // Sweep orphans once per page load (window-level guard preserved from
    // the section IIFEs). Defer slightly so the SideCart / CartPage
    // controllers initialize first and so we don't race the initial section
    // load on cart pages.
    if (!window.__babeinaCartOrphansSwept) {
      window.__babeinaCartOrphansSwept = true;
      setTimeout(sweepOrphans, 200);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
